import { CartSnapshot } from "@dashboard/cartVisibility/types";
import { cartVisibilityUrl } from "@dashboard/cartVisibility/urls";
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import { getPrevLocationState } from "@dashboard/hooks/useBackLinkWithState";
import useLocale from "@dashboard/hooks/useLocale";
import { ListProps, SortPage } from "@dashboard/types";
import { Item } from "@glideapps/glide-data-grid";
import { Box } from "@saleor/macaw-ui-next";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";

import {
  CartVisibilityListSortField,
  cartVisibilityListStaticColumnsAdapter,
  createGetCellContent,
} from "./datagrid";
import { messages } from "./messages";

interface CartVisibilityListDatagridProps extends ListProps, SortPage<CartVisibilityListSortField> {
  carts: CartSnapshot[];
  hasRowHover?: boolean;
  onRowClick?: (id: string) => void;
  onTakeOver: (token: string) => void;
  onOpenDraft: (id: string) => void;
}

export const CartVisibilityListDatagrid = ({
  carts,
  disabled,
  settings,
  sort,
  onSort,
  onUpdateListSettings,
  onRowClick,
  onTakeOver,
  onOpenDraft,
  hasRowHover,
}: CartVisibilityListDatagridProps) => {
  const location = useLocation();
  const intl = useIntl();
  const { locale } = useLocale();
  const datagridState = useDatagridChangeState();

  const handleColumnChange = useCallback(
    (picked: string[]) => {
      if (onUpdateListSettings) {
        onUpdateListSettings("columns", picked.filter(Boolean));
      }
    },
    [onUpdateListSettings],
  );

  const memoizedStaticColumns = useMemo(
    () => cartVisibilityListStaticColumnsAdapter(intl, sort),
    [intl, sort],
  );

  const { handlers, staticColumns, visibleColumns, selectedColumns } = useColumns({
    gridName: "cart_visibility_list",
    staticColumns: memoizedStaticColumns,
    selectedColumns: settings?.columns ?? [],
    onSave: handleColumnChange,
  });

  const handleHeaderClick = useCallback(
    (col: number) => {
      const columnId = visibleColumns[col]?.id as CartVisibilityListSortField;

      if (columnId && columnId !== ("status" as CartVisibilityListSortField)) {
        onSort(columnId);
      }
    },
    [visibleColumns, onSort],
  );

  const getCellContent = useCallback(
    createGetCellContent({
      carts,
      columns: visibleColumns,
      locale,
      anonymousLabel: intl.formatMessage(messages.anonymous),
      notPromotedLabel: intl.formatMessage(messages.notPromoted),
    }),
    [carts, visibleColumns, locale, intl],
  );

  const handleRowClick = useCallback(
    ([_, row]: Item) => {
      if (!onRowClick) {
        return;
      }

      const rowData = carts[row];

      onRowClick(rowData.id);
    },
    [onRowClick, carts],
  );

  const handleRowAnchor = useCallback(([, row]: Item) => cartVisibilityUrl(carts[row].id), [carts]);

  return (
    <DatagridChangeStateContext.Provider value={datagridState}>
      <Datagrid
        readonly
        rowMarkers="none"
        columnSelect="single"
        freezeColumns={1}
        hasRowHover={hasRowHover}
        loading={disabled}
        availableColumns={visibleColumns}
        verticalBorder={false}
        getCellContent={getCellContent}
        getCellError={() => false}
        menuItems={index => {
          const cart = carts[index];

          if (!cart) {
            return [];
          }

          const { draftOrder } = cart;

          return draftOrder
            ? [
                {
                  label: intl.formatMessage(messages.openDraft, {
                    number: draftOrder.number,
                  }),
                  onSelect: () => onOpenDraft(draftOrder.id),
                },
              ]
            : [
                {
                  label: intl.formatMessage(messages.takeOver),
                  onSelect: () => onTakeOver(cart.checkoutToken),
                },
              ];
        }}
        emptyText={intl.formatMessage(messages.emptyText)}
        rows={carts?.length ?? 0}
        selectionActions={() => null}
        onColumnResize={handlers.onResize}
        onColumnMoved={handlers.onMove}
        onHeaderClicked={handleHeaderClick}
        onRowClick={handleRowClick}
        rowAnchor={handleRowAnchor}
        renderColumnPicker={() => (
          <ColumnPicker
            staticColumns={staticColumns}
            selectedColumns={selectedColumns}
            onToggle={handlers.onToggle}
          />
        )}
        navigatorOpts={{ state: getPrevLocationState(location) }}
      />

      <Box paddingX={6}>
        <TablePaginationWithContext
          component="div"
          colSpan={1}
          settings={settings}
          disabled={disabled}
          onUpdateListSettings={onUpdateListSettings}
        />
      </Box>
    </DatagridChangeStateContext.Provider>
  );
};
